// src/components/Footer.jsx
import React from "react";
import {Link} from "react-router-dom";
import useSiteNav from "../hooks/useSiteNav";
import { handleExternalAnchorClick, isNativeApp } from "../utils/nativeNavigation";
import SITE_NAV_FALLBACK from "../config/siteNavFallback";
import "./site-nav.css";

// Voci mostrate nel footer compatto di web-mobile (oltre a social + copyright).
// Solo queste, nell'ordine indicato: il resto delle colonne resta desktop-only.
const MOBILE_ESSENTIAL_LABELS = ["Privacy", "Condizioni generali", "FAQ"];

function pickMobileEssentialLinks(columns) {
  const allItems = columns.flatMap((col) => col.items || []);
  return MOBILE_ESSENTIAL_LABELS.map((label) =>
    allItems.find(
      (it) => String(it.label || "").trim().toLowerCase() === label.toLowerCase(),
    ),
  ).filter(Boolean);
}

// Glifo mostrato dentro il cerchietto social (come sul sito www.stirup.it).
// La chiave e' la "platform" letta da aria-label / classe si-* del sito.
const SOCIAL_GLYPH = {
  facebook: "f",
  instagram: "in",
  twitter: "x",
  x: "x",
  youtube: "▶",
  linkedin: "in",
  tiktok: "t",
};

function glyphFor(platform) {
  const key = String(platform || "").toLowerCase();
  return SOCIAL_GLYPH[key] || key.charAt(0).toUpperCase() || "•";
}

// Copie bundled dei badge (client/public/img/badges): usate se l'immagine
// letta dal sito non carica.
const STORE_BADGE_BUNDLED = {
  apple: "/img/badges/app-store-it.svg",
  google: "/img/badges/google-play-it.png",
};

function handleBadgeError(e, store) {
  const img = e.currentTarget;
  const bundled = STORE_BADGE_BUNDLED[store];
  // una sola sostituzione, niente loop se manca anche la copia bundled
  if (!bundled || img.dataset.fallback) return;
  img.dataset.fallback = "1";
  img.src = bundled;
}

/**
 * Footer dell'app, coerente col footer del sito www.stirup.it.
 *
 * - Voci lette da useSiteNav().footer (server -> cache -> fallback bundled).
 * - Link esterni: nuova scheda su web, browser di sistema su nativo.
 * - Copyright: hardcoded, con override opzionale se il JSON fornisce `copy`.
 * - Montato in MainLayout (non sulle pagine di auth).
 *
 * Tre contesti gestiti da QUESTO componente, nessuna duplicazione:
 * - App nativa (isNativeApp()): non renderizza nulla, l'utente e' dentro
 *   l'app e il footer-vetrina del sito non ha senso li'.
 * - Web desktop (>=1024px): footer completo a colonne, invariato.
 * - Web mobile (<1024px): stesso markup nel DOM, ma site-nav.css nasconde
 *   le colonne complete e mostra solo `.footer-mobile-links` (pochi link
 *   essenziali) sopra a social + copyright. Resta sopra la bottom bar fissa
 *   (vedi regola dello spaziatore in site-nav.css).
 *
 * Badge App Store / Google Play (footer.stores): solo web, tra social e
 * copyright; su web-mobile vanno su una riga propria (site-nav.css).
 */
export default function Footer() {
  const { footer } = useSiteNav();

  if (isNativeApp()) return <nav className="privacy-preferences-access" aria-label="Privacy"><Link to="/privacy/preferenze">Privacy e preferenze</Link></nav>;

  const columns = Array.isArray(footer?.columns) ? footer.columns : [];
  const social = Array.isArray(footer?.social) ? footer.social : [];
  // Badge App Store / Google Play: SOLO web. Controllo esplicito anche se il
  // footer intero non viene gia' reso su nativo (vedi return sopra).
  const stores =
    !isNativeApp() && Array.isArray(footer?.stores)
      ? footer.stores.filter((s) => s?.url && s?.img)
      : [];
  const copy = footer?.copy || SITE_NAV_FALLBACK.footer.copy;

  // Difensivo: senza colonne non ha senso renderizzare (non dovrebbe mai
  // succedere, c'e' il fallback).
  if (columns.length === 0) return null;

  const mobileLinks = pickMobileEssentialLinks(columns);

  return (
    <footer className="stir-footer" role="contentinfo">
      <div className="footer-inner">
        <div className="footer-columns">
          {columns.map((col, ci) => (
            <div className="footer-col" key={col.title || ci}>
              <h4>{col.title}</h4>
              <ul>
                {(col.items || []).map((it, ii) => (
                  <li key={it.url || `${ci}-${ii}`}>
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => handleExternalAnchorClick(e, it.url)}
                    >
                      {it.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {mobileLinks.length > 0 && (
          <nav className="footer-mobile-links" aria-label="Link utili">
            {mobileLinks.map((it, i) => (
              <a
                key={it.url || i}
                href={it.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => handleExternalAnchorClick(e, it.url)}
              >
                {it.label}
              </a>
            ))}
          </nav>
        )}

        <div className="footer-bottom">
          <div className="footer-social">
            {social.map((s, si) => (
              <a
                key={s.url || si}
                className={`social-icon si-${String(s.platform || "").toLowerCase()}`}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                aria-label={s.platform || "social"}
                onClick={(e) => handleExternalAnchorClick(e, s.url)}
              >
                <span>{glyphFor(s.platform)}</span>
              </a>
            ))}
          </div>

          {stores.length > 0 && (
            <div className="footer-stores">
              {stores.map((s, i) => {
                const store = String(s.store || "").toLowerCase();
                return (
                  <a
                    key={s.url || i}
                    className={`store-badge store-${store || "other"}`}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label || s.alt || undefined}
                  >
                    <img
                      src={s.img}
                      alt={s.alt || s.label || ""}
                      loading="lazy"
                      onError={(e) => handleBadgeError(e, store)}
                    />
                  </a>
                );
              })}
            </div>
          )}

          <Link to="/privacy/preferenze" className="privacy-preferences-access">Privacy e preferenze</Link>
          <div className="footer-copy">{copy}</div>
        </div>
      </div>
    </footer>
  );
}
